import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import Sidebar from '../../components/DashboardSidebar';
import { Menu } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function BillingHistory() {
  const auth = useContext(AuthContext);
  const token = auth?.user?.token;
  const [transactions, setTransactions] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lang, setLang] = useState(auth?.user?.language || 'en');

  useEffect(() => {
    const loadTransactions = async () => {
      try {
        const res = await fetchWithAuth(`${process.env.REACT_APP_API_BASE}/api/payment/transactions`);
        const data = await res.json();
        setTransactions(data);
      } catch (err) {
        console.error('Error loading billing history:', err);
        toast.error('Failed to load billing history');
      }
    };
    if (token) loadTransactions();
  }, [token]);

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ToastContainer position="top-right" />

      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} fixed md:relative md:block z-40 w-64`}>
        <Sidebar
          currentLang={lang}
          activeSection="account"
          onLangChange={setLang}
          onCloseMobile={() => setSidebarOpen(false)}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        {/* Mobile Hamburger */}
        <button onClick={() => setSidebarOpen(true)} className="block md:hidden mb-4">
          <Menu className="w-6 h-6 text-gray-700" />
        </button>

        <h2 className="text-3xl font-semibold mb-6 text-gray-800">Billing History</h2>

        {/* Billing Table */}
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded shadow-md">
            <thead>
              <tr className="bg-gray-200 text-gray-700 text-sm">
                <th className="py-3 px-4 text-left">Date</th>
                <th className="py-3 px-4 text-left">Plan</th>
                <th className="py-3 px-4 text-left">Amount</th>
                <th className="py-3 px-4 text-left">Currency</th>
                <th className="py-3 px-4 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length > 0 ? (
                transactions
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) // Latest first
                  .map((tx, idx) => (
                    <tr key={idx} className="border-t text-sm text-gray-700">
                      <td className="py-3 px-4">{new Date(tx.timestamp).toLocaleDateString()}</td>
                      <td className="py-3 px-4">{tx.planName || 'N/A'}</td>
                      <td className="py-3 px-4">{tx.amountTotal?.toFixed(2) || '0.00'}</td>
                      <td className="py-3 px-4">{tx.currency?.toUpperCase() || 'USD'}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs ${
                            tx.paymentStatus === 'paid'
                              ? 'bg-green-100 text-green-700'
                              : tx.paymentStatus === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {tx.paymentStatus}
                        </span>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-6 text-gray-500">
                    No billing history found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
